'use client';
import { platformLabels, type Platform, type Product } from '@curio/contracts';
import { useEffect, useRef, useState } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { catalogSchema, parseCategories } from '@curio/contracts';
import { api, normalize, useAccount, event } from '@curio/client';
import { Loading, Empty, ErrorMessage } from '@curio/ui';
import { SignInPrompt } from '../components/products';
import {
  CategoryRow,
  effectiveCategories,
  filterKey,
  noFilter,
  type CategoryFilter,
} from './category-row';
import { LoadMore, ProductGrid } from './product-grid';
export function Browse({
  saved = false,
  embedded = false,
  initial,
  initialKey,
}: {
  saved?: boolean;
  embedded?: boolean;
  initial?: { products: Product[]; nextCursor: string | null };
  initialKey?: string;
}) {
  const params = useSearchParams();
  const client = useQueryClient();
  const account = useAccount();
  // The categories page browses everything; interests shape the home page instead.
  const [picked, setPicked] = useState<CategoryFilter | null>(null);
  const [search, setSearch] = useState(params.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(timer);
  }, [search]);
  const [sort, setSort] = useState(params.get('sort') || 'popular');
  const [platform, setPlatform] = useState('');
  const queryParam = params.get('q') || '';
  const sortParam = params.get('sort') || 'popular';
  const categoryParam = params.get('categories') ?? params.get('category');
  useEffect(() => {
    setSearch(queryParam);
  }, [queryParam]);
  useEffect(() => setSort(sortParam), [sortParam]);
  useEffect(() => setPicked(null), [categoryParam]);
  const fromUrl = parseCategories(categoryParam);
  const filter =
    picked ??
    (fromUrl.length ? { forYou: false, selected: fromUrl } : noFilter);
  const cats = effectiveCategories(filter, []);
  const key = filterKey(cats);
  const query = useInfiniteQuery({
    queryKey: [
      'catalog',
      key,
      debouncedSearch,
      sort,
      platform,
      saved,
      saved ? account.data?.id : null,
    ],
    enabled: !saved || !!account.data,
    initialPageParam: '',
    initialData:
      !saved &&
      !platform &&
      initial &&
      initialKey === JSON.stringify([key, debouncedSearch, sort])
        ? { pages: [initial], pageParams: [''] }
        : undefined,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }) => {
      const q = new URLSearchParams({
        sort,
        ...(saved ? { saved: 'true' } : {}),
        ...(key ? { categories: key } : {}),
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        ...(platform ? { platform } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      });
      const data = await api('/catalog?' + q, {}, catalogSchema);
      normalize(client, data.products);
      return data;
    },
    getNextPageParam: (p) => p.nextCursor ?? undefined,
  });
  const reportedSearch = useRef('');
  useEffect(() => {
    if (!search) {
      reportedSearch.current = '';
      return;
    }
    if (!query.data || query.isFetching || search.trim() !== debouncedSearch)
      return;
    const reportKey = JSON.stringify([search, key, sort, platform, saved]);
    const timer = setTimeout(() => {
      if (reportedSearch.current === reportKey) return;
      reportedSearch.current = reportKey;
      const first = query.data.pages[0];
      event('search_completed', undefined, {
        query_length: search.length,
        result_count: first.products.length,
        has_more: !!first.nextCursor,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [
    search,
    debouncedSearch,
    key,
    sort,
    platform,
    saved,
    query.data,
    query.isFetching,
  ]);
  if (saved && !account.data)
    return account.isPending ? <Loading /> : <SignInPrompt />;
  const products =
    query.data?.pages
      .flatMap((p) => p.products)
      .filter((p) => !saved || account.data?.savedIds.includes(p.id)) ?? [];
  return (
    <div className={embedded ? 'profile-tab-panel' : 'page'}>
      <CategoryRow filter={filter} onChange={setPicked} personal={false} />
      <div className="browse-heading">
        {!embedded && (
          <h1>
            {saved
              ? 'Saved products'
              : search
                ? `Results for “${search}”`
                : cats.length === 1
                  ? cats[0]
                  : 'Browse products'}
          </h1>
        )}
        <input
          className={
            saved ? 'browse-search' : 'browse-search browse-search-mobile'
          }
          type="search"
          aria-label="Search products"
          placeholder="Search apps, tools, and games"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="browse-filters">
          <select
            aria-label="Platform"
            value={platform}
            onChange={(e) => {
              setPlatform(e.target.value);
              event('catalog_filter_changed', undefined, {
                filter: 'platform',
                value: e.target.value || 'all',
              });
            }}
          >
            <option value="">All platforms</option>
            {[
              'web',
              'pwa',
              'ios',
              'android',
              'macos',
              'windows',
              'linux',
              'steam',
              'chrome',
              'zen',
              'safari',
              'firefox',
            ].map((p) => (
              <option key={p} value={p}>
                {platformLabels[p as Platform]}
              </option>
            ))}
          </select>
          <select
            aria-label="Sort products"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              event('catalog_filter_changed', undefined, {
                filter: 'sort',
                value: e.target.value,
              });
            }}
          >
            <option value="popular">Popular</option>
            <option value="new">New</option>
            <option value="trending">Trending</option>
            <option value="hidden">Hidden gems</option>
          </select>
        </div>
      </div>
      <div
        style={{ marginTop: 24 }}
        className={query.isPlaceholderData ? 'is-stale' : ''}
      >
        {query.isPending ? (
          <Loading />
        ) : query.error && !query.data ? (
          <ErrorMessage error={query.error} />
        ) : products.length ? (
          <ProductGrid products={products} />
        ) : (
          <Empty title={saved ? 'No saved products' : 'No matches'}>
            {saved
              ? 'Save a product to find it here.'
              : 'Try another category or search term.'}
          </Empty>
        )}
      </div>
      <LoadMore query={query} />
    </div>
  );
}
